import { cn, formatArrayAsString } from "@/lib/utils";
import {
  CSSProperties,
  KeyboardEvent,
  PropsWithChildren,
  forwardRef,
  useMemo,
  useEffect,
  useState,
} from "react";
import { ClassNameValue } from "tailwind-merge";
import { ContainerMenu } from "./ContainerMenu";
import { ContainerDialog } from "./ContainerDialog";
import { toContainerFormValues } from "./ContainerForm";
import { useStore } from "@/stores/useStore";
import { formatLabels } from "@/lib/cards";
import { useContainerConfigurationCheck } from "@/hooks/useContainerConfigurationCheck";
import { Icon } from "@/components/Common/Icon";
import { CheckCircle2 } from "lucide-react";
import { Skeleton } from "@/components/Base/Skeleton";
import { Container, FEATURE_FLAGS } from "@/types/v2";
import {
  normalizeLabels,
  getBidirectionalTechnicianIds,
} from "@/lib/checkContainerConfiguration";
import { container as containerRepository } from "@/repositories/container";
import { useFeature } from "flagged";
import { useFeature as useShopFeature } from "@/hooks/useFeatures";
import { isTechnicianWritebackEnabledForShop } from "@/lib/technicianWriteback";
import { getIntegrationName } from "@/lib/integration";
import { getLinkableLabels } from "@/lib/getLinkableLabels";
import { isBulkSelectable } from "@/lib/containerSelection";

export interface LayoutContainerProps extends PropsWithChildren {
  removeItem: (id: string) => void;
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  style?: CSSProperties;
  className?: ClassNameValue;
  editable?: boolean;
  bulkEditMode?: boolean;
  selected?: boolean;
  onSelectToggle?: (id: string) => void;
}

const LayoutContainer = forwardRef<HTMLDivElement, LayoutContainerProps>(
  (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    {
      removeItem,
      style,
      className,
      children,
      id,
      x,
      y,
      w,
      h,
      editable,
      bulkEditMode,
      selected,
      onSelectToggle,
      ...restOfProps
    },
    ref
  ) => {
    const { shop, employees } = useStore();
    const containers = useMemo(() => shop?.containers ?? [], [shop]);
    const container = containers.find((c) => c.id === id);
    const labels = useMemo(() => (shop ? getLinkableLabels(shop) : []), [shop]);
    const removeOnly = id === "dispatch";
    const isTechnicianLinkEnabled = !!useFeature("isTechnicianLinkEnabled");
    const isROLabelLinkEnabled = !!useFeature("isROLabelLinkEnabled");
    const integrationName = getIntegrationName(shop);
    const technicianWritebackEnabled = shop
      ? isTechnicianWritebackEnabledForShop(shop)
      : false;
    const [editOpen, setEditOpen] = useState(false);
    const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);
    const isBidirectionalWritebackEnabled = useShopFeature(
      FEATURE_FLAGS.isROLabelWritebackEnabled
    );
    const labelBidirectionalSyncActive =
      normalizeLabels(container?.bidirectionalLabel).length > 0 &&
      isBidirectionalWritebackEnabled;
    const { valid, runCheck } = useContainerConfigurationCheck(id);

    const handleEditSubmit = async (values: Partial<Container>) => {
      setIsSubmittingEdit(true);
      await containerRepository.updateContainer(id, values);
      setIsSubmittingEdit(false);
      setEditOpen(false);
    };

    const containerName = useMemo(
      () => container?.displayName ?? id,
      [container, id]
    );

    const technicianId = useMemo(
      () => container?.linkedTechnicianId,
      [container]
    );

    const sourceLabel = useMemo(
      () => container?.linkedSourceLabel,
      [container]
    );

    useEffect(() => {
      if (container) {
        runCheck({
          linkedTechnicianId: container.linkedTechnicianId,
          linkedSourceLabel: container.linkedSourceLabel,
        });
      }
    }, [container, containers, runCheck]);

    const technicians = useMemo(() => {
      if (
        typeof technicianId === "string" ||
        typeof technicianId === "number"
      ) {
        return employees?.filter((emp) => `${emp.id}` === technicianId);
      }
      return employees?.filter((emp) => technicianId?.includes(`${emp.id}`));
    }, [employees, technicianId]);

    // Intersected against `technicians` (the currently-resolvable linked
    // employees), not just the raw ids from the container -- otherwise a
    // stale bidirectional id pointing at a removed/hidden employee would
    // turn the border green with no Technicians row (and thus no icon)
    // anywhere on the tile to explain why.
    const technicianBidirectionalSyncActive =
      getBidirectionalTechnicianIds(container).some((techId) =>
        technicians?.some((emp) => `${emp.id}` === techId)
      ) && technicianWritebackEnabled;
    const bidirectionalSyncActive =
      labelBidirectionalSyncActive || technicianBidirectionalSyncActive;

    // The app shell (BasePage) hardcodes bg-slate-900, but nothing in the app ever
    // applies the `dark` class -- so every semantic token resolves to the LIGHT
    // palette. There, --secondary IS slate-900 and --primary is blue-800, which
    // made these tiles the same color as the page they sit on (WEBV3-178).
    //
    // The slate values are deliberate and must not be turned back into tokens.
    // They mirror the real board's containers (Dashboard.tsx -> SortableContainer,
    // `bg-slate-800 rounded-lg shadow`) so the builder previews what the board
    // actually looks like. The border is the one addition: unlike the board, the
    // builder is an editing surface where tile bounds are load-bearing, and the
    // slate-800 fill alone is only ~1.4:1 against the recessed slate-950 scroll
    // well it sits on (LayoutCreator.tsx).
    //
    // Defined once because both return branches below render this same surface.
    const bulkUnselectable = !!bulkEditMode && !isBulkSelectable(id);
    const surfaceClasses = cn(
      bulkUnselectable
        ? "cursor-not-allowed opacity-55"
        : bulkEditMode
          ? "cursor-pointer"
          : "cursor-grab",
      // Rapid selecting clicks would otherwise highlight the tile's text.
      bulkEditMode && "select-none",
      "rounded-lg p-2 shadow",
      valid
        ? cn(
            "border bg-slate-800 text-slate-100",
            bidirectionalSyncActive ? "border-emerald-300" : "border-slate-500"
          )
        : "bg-destructive/70 text-destructive-foreground",
      selected && "ring-2 ring-offset-2 ring-offset-slate-950 ring-blue-400",
      className
    );

    if (!editable) {
      return (
        <Skeleton>
          <div
            style={{ ...style }}
            className={surfaceClasses}
            ref={ref}
            {...restOfProps}
          ></div>
        </Skeleton>
      );
    }
    // In bulk-edit mode a tile is a checkbox: focusable, toggled with
    // Enter/Space, and its selected state announced (the ring and check icon
    // are visual only). The dispatch tile is exposed as a disabled one.
    const bulkA11yProps = bulkEditMode
      ? bulkUnselectable
        ? { role: "checkbox", "aria-checked": false, "aria-disabled": true }
        : {
            role: "checkbox",
            "aria-checked": !!selected,
            tabIndex: 0,
            onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
              if (e.key !== "Enter" && e.key !== " ") return;
              e.preventDefault();
              onSelectToggle?.(id);
            },
          }
      : {};
    return (
      <div
        style={{ ...style }}
        className={surfaceClasses}
        ref={ref}
        onClick={
          bulkEditMode && !bulkUnselectable
            ? () => onSelectToggle?.(id)
            : undefined
        }
        {...bulkA11yProps}
        {...restOfProps}
      >
        {editable && bulkEditMode && selected && (
          // Occupies the same top-right corner ContainerMenu's kebab uses
          // when not in bulk mode -- the two are mutually exclusive (the
          // kebab menu is hidden in bulk mode), so there's no overlap.
          <CheckCircle2
            className={
              "absolute right-1 top-1 h-6 w-6 flex-shrink-0 text-blue-400"
            }
          />
        )}
        {editable && !bulkEditMode && (
          <>
            <ContainerMenu
              containerId={id}
              removeItem={removeItem}
              onEdit={() => setEditOpen(true)}
              triggerClassName={
                valid ? undefined : "text-destructive-foreground"
              }
            />
            <ContainerDialog
              containerData={toContainerFormValues(
                container ?? { id, tags: [] }
              )}
              employees={
                !removeOnly && isTechnicianLinkEnabled ? employees : undefined
              }
              labels={!removeOnly && isROLabelLinkEnabled ? labels : undefined}
              labelCatalog={shop?.labelCatalog}
              containers={containers}
              integrationName={integrationName}
              technicianWritebackEnabled={technicianWritebackEnabled}
              open={editOpen}
              onOpenChange={setEditOpen}
              onSubmit={handleEditSubmit}
              loading={isSubmittingEdit}
              externalTrigger
            />
          </>
        )}
        <div className={"flex flex-row gap-1"}>
          {!valid && (
            <Icon
              name={"warning"}
              className={"h-6 w-6 flex-shrink-0 text-destructive-foreground"}
            />
          )}
          {containerName}
        </div>
        {bulkUnselectable && (
          <div className={"text-xs text-slate-300"}>Can't be linked</div>
        )}
        {technicians && technicians.length > 0 && (
          <div className={"flex flex-row items-center gap-1"}>
            {valid && technicianBidirectionalSyncActive && (
              <Icon
                name={"bidirectional"}
                className={"h-6 w-6 flex-shrink-0 text-emerald-300"}
              />
            )}
            <span>
              {`Technician${technicians.length > 1 ? "s" : ""}: `}
              {formatArrayAsString(
                technicians.map(
                  (technician) =>
                    `${technician?.firstName} ${technician.lastName}`
                )
              )}
            </span>
          </div>
        )}
        {sourceLabel && sourceLabel.length > 0 && (
          <div className={"flex flex-row items-center gap-1"}>
            {valid && labelBidirectionalSyncActive && (
              <Icon
                name={"bidirectional"}
                className={"h-6 w-6 flex-shrink-0 text-emerald-300"}
              />
            )}
            <span>{`Label${
              typeof sourceLabel === "string"
                ? ""
                : sourceLabel.length > 1
                  ? "s"
                  : ""
            }: ${formatLabels(sourceLabel)}`}</span>
          </div>
        )}
        {children}
      </div>
    );
  }
);

LayoutContainer.displayName = "LayoutContainer";

export default LayoutContainer;
